import type { CatalogResultCard } from './types'

/**
 * The configurator core's typed contract (UID-34 / ADR-0098 Decision 2/3) —
 * re-exported off the generated wire type rather than hand-rolled, so a
 * contract change to `CatalogConfigSchema` can never silently drift from
 * what this module validates/composes against.
 *
 * ADR-0098 generalizes the wire shape from a scalar
 * `{ prefix, resolvable, multiDimension, dimension? }` to an ordered
 * `segments` list (`literal | range | enum | raw`, ADR-0098 Decision 3). The
 * ADR-0097 single-dimension case is exactly N=1: one dimension segment,
 * possibly with leading/trailing literals. Every function below is written
 * as the general per-segment fold — there is no special-cased N=1 branch.
 */
export type ConfigSchema = NonNullable<CatalogResultCard['configSchema']>
export type ConfigSegment = ConfigSchema['segments'][number]
/** The subset of {@link ConfigSegment} that actually needs a picked value. */
export type ConfigDimensionSegment = Extract<ConfigSegment, { kind: 'range' | 'enum' }>

/**
 * The picks map: one entry per dimension segment's INDEX INTO `segments`
 * (not a 0..N-1 dimension-only index) — keyed by segment index so a picked
 * value can never be misaligned with its segment when segments are
 * interleaved with literals.
 */
export type SkuPicks = ReadonlyMap<number, number>

export type ConfiguratorGate =
  | { readonly kind: 'pickable'; readonly dimensionSegments: readonly ConfigDimensionSegment[] }
  | { readonly kind: 'disabled' }

/**
 * The ADR-0098 gate: a `configSchema` only backs a working picker when it is
 * `resolvable` (derived server-side as "no `raw` segment present") — the
 * honest DISABLED state (ADR-0097 §8.3, now also triggered by ANY `raw`
 * segment, not just a non-single-dimension family) otherwise. One pure
 * function so every caller (Surface A page, Surface B drawer) agrees.
 */
export function gateConfigSchema(schema: ConfigSchema): ConfiguratorGate {
  const hasRaw = schema.segments.some((segment) => segment.kind === 'raw')
  if (!schema.resolvable || hasRaw) {
    return { kind: 'disabled' }
  }
  const dimensionSegments = schema.segments.filter(
    (segment): segment is ConfigDimensionSegment =>
      segment.kind === 'range' || segment.kind === 'enum',
  )
  if (dimensionSegments.length === 0) {
    return { kind: 'disabled' }
  }
  return { kind: 'pickable', dimensionSegments }
}

/**
 * Client-side mirror of the backend's `resolveSku` in-range/on-step/in-enum
 * checks (ADR-0097 8.4 step 2, generalized per-slot by ADR-0098) — UX-only
 * defense-in-depth; the resolve call remains authoritative. `± 1e-9` on the
 * step check matches the backend's own float-tolerance (`configurable-family.ts`
 * `isOnStep`). Validates ONLY against this segment's own bounds — a legal
 * pick for one dimension slot says nothing about any other slot.
 */
export function isValueLegal(segment: ConfigDimensionSegment, value: number): boolean {
  if (!Number.isFinite(value)) return false
  if (segment.kind === 'range') {
    const { min, max, step } = segment
    if (step <= 0) return false
    if (value < min || value > max) return false
    const quotient = (value - min) / step
    return Math.abs(quotient - Math.round(quotient)) < 1e-9
  }
  return segment.values.includes(value)
}

function decimalPlaces(n: number): number {
  const str = String(n)
  const dot = str.indexOf('.')
  return dot === -1 ? 0 : str.length - dot - 1
}

/**
 * Round a computed value back to the decimal precision implied by `places`
 * (derived from a `step`'s own string form via {@link decimalPlaces}) —
 * `round(value * 10^places) / 10^places`. This is what turns a
 * `min + stepIndex * step` binary-float artifact like `10.799999999999999`
 * back into an exact `10.8` BEFORE the value is ever stored in state,
 * compared, or displayed — not just at the final string-formatting step
 * ({@link formatValueToken}), which is too late once an imprecise number is
 * already sitting in `picks`.
 */
function roundToDecimalPlaces(value: number, places: number): number {
  const scale = 10 ** places
  return Math.round(value * scale) / scale
}

function trimTrailingZeros(numeric: string): string {
  if (!numeric.includes('.')) return numeric
  return numeric.replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * Format a legal picked value as MISUMI's bracket-token style
 * (`/^\d+(?:\.\d+)?$/` — no trailing zeros, no locale separators; ADR-0097
 * 8.4 step 3). Decimal places are derived from the dimension's own step (a
 * `range`) or the value's own literal precision (an `enum`, whose members
 * are already-legal wire numbers), then the value is round-tripped through
 * integer-scaled arithmetic (`round(value * 10^places) / 10^places`) before
 * stringifying — this is what turns a `4.5` float artifact like
 * `4.499999999999999` back into an exact `"4.5"`, and what guarantees a
 * whole-number step (`places = 0`) never renders `"59.0"`.
 */
export function formatValueToken(segment: ConfigDimensionSegment, value: number): string {
  const places = segment.kind === 'range' ? decimalPlaces(segment.step) : decimalPlaces(value)
  const scaled = roundToDecimalPlaces(value, places)
  return trimTrailingZeros(scaled.toFixed(places))
}

/**
 * `sku = concat(segments)` — walk `segments` in template order, emitting
 * each `literal.text` verbatim and each dimension segment's picked-and-
 * formatted value (ADR-0098 Decision 2). Refuses to compose (returns `null`)
 * until every dimension slot in `picks` has a legal value for ITS OWN
 * segment — no fabrication: literals are verbatim, values are validated
 * per-slot, nothing is guessed for a missing pick.
 */
export function composeSku(schema: ConfigSchema, picks: SkuPicks): string | null {
  const tokens: string[] = []
  for (const [index, segment] of schema.segments.entries()) {
    if (segment.kind === 'literal') {
      tokens.push(segment.text)
      continue
    }
    if (segment.kind === 'raw') return null
    const value = picks.get(index)
    if (value === undefined || !isValueLegal(segment, value)) return null
    tokens.push(formatValueToken(segment, value))
  }
  return tokens.join('')
}

/**
 * The full ADR-0098 client-side pipeline: gate → validate every slot →
 * compose. Returns `null` when the schema isn't pickable or any dimension
 * slot is missing/illegal — callers use `null` to mean "don't call the
 * resolve seam yet," never a fabricated SKU. A non-null return is a SKU the
 * resolve endpoint is still the sole authority over (this function never
 * asserts the SKU is real — only that it is legal-by-construction to ask
 * about).
 */
export function validateAndComposeSku(schema: ConfigSchema, picks: SkuPicks): string | null {
  const gate = gateConfigSchema(schema)
  if (gate.kind !== 'pickable') return null
  return composeSku(schema, picks)
}

type RangeSegment = Extract<ConfigDimensionSegment, { kind: 'range' }>

/**
 * The visible cap on how many quick-pick preset buttons a `range` dimension
 * renders (visual parity with the reference mock's chip row, which showed 7
 * for one specific part). OPEN QUESTION, flagged rather than silently
 * assumed: the reference image doesn't disambiguate the "right" count for
 * every real min/max/step combination this renders against — this is this
 * engineer's mechanical choice pending product/design confirmation, not a
 * value read off the mock for every part.
 */
export const MAX_RANGE_PRESETS = 7

/**
 * A mechanically-derived, always-in-range/on-step subset of a `range`
 * segment's OWN legal values, offered as quick-pick shortcuts — visual
 * parity with the `enum` pills, never a fixed/fabricated "standard sizes"
 * list (ADR-0090/0091: real data authoritative, nothing fabricated).
 *
 * Algorithm: walk the segment's own step lattice (`min + i*step` for
 * `i = 0..(max-min)/step`) and take up to {@link MAX_RANGE_PRESETS} points
 * evenly spaced BY STEP INDEX (not by raw value — irrelevant here since the
 * lattice is uniform, but keeps every point exactly on-step by construction,
 * no float snapping needed). The first and last points are forced to the
 * segment's own `min`/`max` (rather than recomputed through the spacing
 * formula) so a float rounding artifact in the spacing math can never
 * produce a near-miss "699.9999999" sitting right next to a `max: 700` —
 * only one exact endpoint value ever appears. Deduplicated, ascending.
 *
 * OPEN QUESTION (see {@link MAX_RANGE_PRESETS}): the "evenly spaced by step
 * index, capped at 7" spacing rule itself is this engineer's mechanical
 * choice, not something the reference mock proves for every span — flagged
 * for confirmation rather than assumed.
 */
export function computeRangePresets(segment: RangeSegment): number[] {
  const { min, max, step } = segment
  if (!(step > 0) || !(max >= min)) return []
  const totalSteps = Math.round((max - min) / step)
  const presetCount = Math.max(1, Math.min(MAX_RANGE_PRESETS, totalSteps + 1))
  // Precision implied by the segment's OWN step (e.g. `step: 0.01` -> 2
  // places) — every lattice point below is rounded back to this precision
  // so `min + stepIndex * step` binary-float noise (`10.799999999999999`
  // instead of `10.8`) never reaches `picks` state in the first place.
  const places = decimalPlaces(step)

  const values = new Set<number>()
  for (let i = 0; i < presetCount; i++) {
    if (i === 0) {
      values.add(min)
      continue
    }
    if (i === presetCount - 1) {
      values.add(max)
      continue
    }
    const fraction = i / (presetCount - 1)
    const stepIndex = Math.round(fraction * totalSteps)
    values.add(roundToDecimalPlaces(min + stepIndex * step, places))
  }
  return Array.from(values).sort((a, b) => a - b)
}
